import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';

interface CanvasViewportOptions {
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
  requireCtrlKey?: boolean;
}

interface CanvasViewportResult {
  zoomLevel: number;
  panPosition: { x: number; y: number };
  isPanning: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  handleMouseDown: (event: ReactMouseEvent) => void;
  handleMouseMove: (event: ReactMouseEvent) => void;
  handleMouseUp: () => void;
}

export const useCanvasViewport = (
  containerRef: RefObject<HTMLElement | null>,
  {
    minZoom = 0.5,
    maxZoom = 3,
    zoomStep = 0.1,
    requireCtrlKey = true,
  }: CanvasViewportOptions = {}
): CanvasViewportResult => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);

  const clampZoom = useCallback(
    (value: number) => Math.min(maxZoom, Math.max(minZoom, value)),
    [maxZoom, minZoom]
  );

  const setPanning = useCallback((value: boolean) => {
    isPanningRef.current = value;
    setIsPanning(value);
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((prevZoom) => clampZoom(prevZoom + zoomStep));
  }, [clampZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    setZoomLevel((prevZoom) => {
      const nextZoom = clampZoom(prevZoom - zoomStep);
      if (nextZoom <= 1) {
        setPanPosition({ x: 0, y: 0 });
      }
      return nextZoom;
    });
  }, [clampZoom, zoomStep]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent) => {
      if (zoomLevel > 1 && containerRef.current?.contains(event.target as Node)) {
        setPanning(true);
        lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
      }
    },
    [containerRef, setPanning, zoomLevel]
  );

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent) => {
      if (!isPanningRef.current) {
        return;
      }

      const deltaX = event.clientX - lastMousePositionRef.current.x;
      const deltaY = event.clientY - lastMousePositionRef.current.y;

      setPanPosition((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));

      lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    },
    []
  );

  const handleMouseUp = useCallback(() => {
    if (isPanningRef.current) {
      setPanning(false);
    }
  }, [setPanning]);

  useEffect(() => {
    const handleScrollZoom = (event: Event) => {
      const e = event as WheelEvent;
      if (requireCtrlKey && !e.ctrlKey) {
        return;
      }

      e.preventDefault();

      if (e.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    };

    const currentContainer = containerRef.current ?? window;
    // addEventListener accepts EventListener; cast to satisfy differing container types
    (currentContainer as any).addEventListener('wheel', handleScrollZoom as EventListener, { passive: false });

    return () => {
      (currentContainer as any).removeEventListener('wheel', handleScrollZoom as EventListener);
    };
  }, [containerRef, requireCtrlKey, zoomIn, zoomOut]);

  return {
    zoomLevel,
    panPosition,
    isPanning,
    zoomIn,
    zoomOut,
    resetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};

export default useCanvasViewport;
